/**
 * Reusable Upgrade Prompt Component
 * Shows upgrade CTAs for free tier users
 */

'use client';

import { useState } from 'react';
import { useFeatureFlags } from '@/lib/useFeatureFlags';
import { useAuth } from '@/lib/auth';
import ComingSoonModal from './ComingSoonModal';

interface UpgradePromptProps {
  feature: string;
  variant?: 'banner' | 'card' | 'inline';
  className?: string;
}

export default function UpgradePrompt({
  feature,
  variant = 'card',
  className = ''
}: UpgradePromptProps) {
  const { flags } = useFeatureFlags();
  const [showModal, setShowModal] = useState(false);
  const baseClasses = 'border-2 border-border rounded-lg bg-surface/50';

  const handleUpgradeClick = () => {
    if (!flags.paymentAllowed) {
      setShowModal(true);
      return;
    }
    // If payment is allowed, redirect to pricing/upgrade page
    window.location.href = '/pricing';
  };

  if (variant === 'banner') {
    return (
      <>
        <div className={`${baseClasses} p-4 flex items-center justify-between ${className}`}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">🔒</div>
            <div>
              <p className="text-foreground font-semibold">Unlock {feature}</p>
              <p className="text-foreground-secondary text-sm">Available on Pro and Team plans</p>
            </div>
          </div>
          <button
            onClick={handleUpgradeClick}
            className="bg-accent text-accent-foreground px-6 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors whitespace-nowrap cursor-pointer"
          >
            Upgrade to Pro
          </button>
        </div>
        <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)} type="upgrade" />
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <span className={`inline-flex items-center gap-2 text-accent text-sm ${className}`}>
          <span>🔒</span>
          <button onClick={handleUpgradeClick} className="underline hover:text-accent cursor-pointer">
            Upgrade to unlock {feature}
          </button>
        </span>
        <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)} type="upgrade" />
      </>
    );
  }

  // Default: card variant
  return (
    <>
      <div className={`${baseClasses} p-6 text-center ${className}`}>
        <div className="text-4xl mb-3">🔒</div>
        <h3 className="text-lg font-bold text-foreground mb-2">Unlock {feature}</h3>
        <p className="text-foreground-secondary text-sm mb-4">
          Available on Pro ($15/mo) and Team ($49/mo) plans
        </p>
        <button
          onClick={handleUpgradeClick}
          className="inline-block bg-accent text-accent-foreground px-6 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors cursor-pointer"
        >
          Upgrade Now
        </button>
      </div>
      <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)} type="upgrade" />
    </>
  );
}

/**
 * Badge for Pro/Team features
 */
export function ProBadge({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent-muted text-accent text-xs font-semibold ${className}`}>
      <span>⭐</span>
      PRO
    </span>
  );
}

/**
 * Sign up prompt for logged-out users
 */
export function SignUpPrompt({
  feature,
  variant = 'card',
  className = ''
}: UpgradePromptProps) {
  const { flags } = useFeatureFlags();
  const { login } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const baseClasses = 'border-2 border-border rounded-lg bg-surface/50';

  const handleSignUpClick = () => {
    if (!flags.signUpAllowed) {
      setShowModal(true);
      return;
    }
    // If signup is allowed, redirect to signup page
    setLoginLoading(true);
    login({ screenHint: 'signup' });
  };

  if (variant === 'banner') {
    return (
      <>
        <div className={`${baseClasses} p-4 flex items-center justify-between ${className}`}>
          <div className="flex items-center gap-3">
            <div className="text-2xl">✨</div>
            <div>
              <p className="text-foreground font-semibold">Sign up to enable {feature}</p>
              <p className="text-foreground-secondary text-sm">Create a free account to get started</p>
            </div>
          </div>
          <button
            onClick={handleSignUpClick}
            disabled={loginLoading}
            className="bg-accent text-accent-foreground px-6 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-wait flex items-center gap-2"
          >
            {loginLoading && (
              <div className="w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
            )}
            Sign Up Free
          </button>
        </div>
        <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)} type="signup" />
      </>
    );
  }

  // Default: card variant
  return (
    <>
      <div className={`${baseClasses} p-6 text-center ${className}`}>
        <div className="text-4xl mb-3">✨</div>
        <h3 className="text-lg font-bold text-foreground mb-2">Sign up to enable {feature}</h3>
        <p className="text-foreground-secondary text-sm mb-4">
          Create a free account to access this feature
        </p>
        <button
          onClick={handleSignUpClick}
          disabled={loginLoading}
          className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-2 rounded-lg font-semibold hover:bg-accent-hover transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-wait"
        >
          {loginLoading && (
            <div className="w-4 h-4 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
          )}
          Sign Up Free
        </button>
      </div>
      <ComingSoonModal isOpen={showModal} onClose={() => setShowModal(false)} type="signup" />
    </>
  );
}
